import { PickupPayer } from "@redotech/redo-return-core-common/return-flow-enums";
import { Big } from "big.js";
import type { DraftReturnShipment } from "./draft-return";

/**
 * A shipment's `pickupFee` is never zeroed when the merchant covers it, so a
 * fee that will never be charged is unrepresentable: it only exists on `customerPaid`.
 */
export type PickupCost =
  | { readonly kind: "merchantCovered" }
  | { readonly kind: "customerPaid"; readonly fee: Big };

/**
 * Pre-submission. Undefined when pickup is not offered for the shipment.
 * Old documents without the flag are customer-paid.
 */
export function pickupCostFromShipment(
  shipment:
    | Pick<
        DraftReturnShipment,
        "pickupFee" | "pickupFeeSet" | "pickupCoveredByMerchant"
      >
    | null
    | undefined,
): PickupCost | undefined {
  const pickupFee =
    shipment?.pickupFeeSet?.shopMoney.amount ?? shipment?.pickupFee;
  if (!pickupFee) {
    return undefined;
  }
  if (shipment?.pickupCoveredByMerchant) {
    return { kind: "merchantCovered" };
  }
  return { kind: "customerPaid", fee: new Big(pickupFee) };
}

/**
 * Post-submission, from the scheduled pickup on the return. A missing rate is
 * a zero charge, matching what the return stores for it.
 */
export function pickupCostFromPickupPayer({
  pickupPayer,
  rate,
}: {
  pickupPayer: string | null | undefined;
  rate: number | null | undefined;
}): PickupCost {
  if (pickupPayer === PickupPayer.MERCHANT) {
    return { kind: "merchantCovered" };
  }
  return { kind: "customerPaid", fee: new Big(rate ?? 0) };
}
